import { Dialog, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  QueueListIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Fragment, useContext, useMemo, useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { FacebookAdsIcon } from '../../assets/svgs/icons/FacebookAdsIcon';
import { GoogleAdsIcon } from '../../assets/svgs/icons/GoogleAdsIcon';
import { AuthContext } from '../../contexts/AuthContext';
import { UserContext } from '../../contexts/UserContext';
import { classNames } from '../../utils';
import DashboardSidebar, {
  DashboardSidebarCollapseMode,
  SidebarItem,
  SidebarTeamItem,
} from './DashboardSidebar';
import MenuButton from './MenuButton';
import Tooltip from './Tooltip';

export default function DashboardLayout() {
  const { slug } = useParams();
  const location = useLocation();
  const { firebaseUser, logout } = useContext(AuthContext);
  const { accounts } = useContext(UserContext);
  // const hasFacebookAdsFeature = useHasFeature('facebookAds');

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false);
  const [collapseMode, setCollapseMode] =
    useState<DashboardSidebarCollapseMode>('open');

  const navigation: SidebarItem[] = useMemo(() => {
    const base = `/workspace/${slug}/`;
    const items: Omit<SidebarItem, 'current'>[] = [
      // { name: 'Dashboard', href: base + 'overview', icon: ChartPieIcon },
      {
        name: 'Search Campaigns',
        href: base + 'search-campaigns',
        icon: GoogleAdsIcon,
      },
      {
        name: 'Social Campaigns',
        href: base + 'social-campaigns',
        icon: FacebookAdsIcon,
      },
      { name: 'Dynamic Pages', href: base + 'pages', icon: QueueListIcon },
      // ...(hasFacebookAdsFeature
      //   ? [
      //       {
      //         name: 'Facebook Ads',
      //         href: base + 'facebook-ads',
      //         icon: FacebookAdsIcon,
      //       },
      //     ]
      //   : []),
      // {
      //   name: 'Integrations',
      //   href: base + 'integrations',
      //   icon: CodeBracketIcon,
      // },
      {
        name: 'Leads',
        href: base + 'leads',
        icon: UsersIcon,
      },
      // {
      //   name: 'Forms',
      //   href: base + 'forms',
      //   icon: TableCellsIcon,
      // },
      // {
      //   name: 'Content',
      //   href: base + 'content',
      //   icon: DocumentTextIcon,
      // },
      // { name: 'Brand', href: base + 'brand', icon: SwatchIcon },
      { name: 'Settings', href: base + 'settings', icon: Cog6ToothIcon },
    ];

    return items.map((item) => ({
      ...item,
      current: location.pathname.startsWith(item.href),
    }));
  }, [location, slug]);

  const teams: SidebarTeamItem[] = useMemo(() => {
    return (
      accounts?.map((account) => {
        const accountData = account.data();
        return {
          id: accountData.slug,
          name: accountData.name,
          href: `/workspace/${accountData.slug}/overview`,
          initial: accountData.name?.[0] || '',
          current: slug === accountData.slug,
        };
      }) ?? []
    );
  }, [slug, accounts]);

  return (
    <div className="flex h-screen flex-col">
      <Transition.Root show={mobileSidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50 lg:hidden"
          onClose={setMobileSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setMobileSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 ring-1 ring-white/10">
                  <DashboardSidebar
                    teams={teams}
                    navigation={navigation}
                    collapseMode={'open'}
                    onCollapseModeChange={() => {}}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div
        className={classNames(
          'hidden overflow-x-hidden transition-all duration-300 lg:fixed lg:inset-y-0 lg:z-10 lg:flex lg:flex-col',
          desktopSidebarCollapsed ? 'lg:w-16' : 'lg:w-60',
        )}
        onMouseEnter={
          collapseMode === 'open-on-hover'
            ? () => setDesktopSidebarCollapsed(false)
            : undefined
        }
        onMouseLeave={
          collapseMode === 'open-on-hover'
            ? () => setDesktopSidebarCollapsed(true)
            : undefined
        }
      >
        <DashboardSidebar
          teams={teams}
          navigation={navigation}
          collapsed={desktopSidebarCollapsed}
          collapseMode={collapseMode}
          onCollapseModeChange={(mode) => {
            if (mode === 'open') {
              setDesktopSidebarCollapsed(false);
            }
            setCollapseMode(mode);
          }}
        />
      </div>

      {/* Sticky sidebar for mobile */}
      <div className="flex h-16 items-center gap-x-6 bg-indigo-600 px-4 py-4 shadow-sm sm:px-6 lg:hidden">
        <button
          type="button"
          className="-m-2.5 p-2.5 text-indigo-200 lg:hidden"
          onClick={() => setMobileSidebarOpen(true)}
        >
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon className="h-6 w-6" aria-hidden="true" />
        </button>
        <div className="flex-1 text-sm font-semibold leading-6 text-white">
          {/* Dashboard */}
        </div>
        <MenuButton
          items={[
            {
              title: 'Sign Out',
              onClick: () => {
                logout();
              },
            },
          ]}
          customButton={
            <img
              className="h-8 w-8 rounded-full bg-indigo-700"
              src={
                firebaseUser?.photoURL ??
                'https://firebasestorage.googleapis.com/v0/b/cyder-ai.appspot.com/o/images%2Favatar.png?alt=media&token=4bf1a9f5-7f6e-4827-a876-f5074ea77e86&_gl=1*y9yqgy*_ga*MTE3MjE2NTUyNS4xNjk2MzE5NDg2*_ga_CW55HF8NVT*MTY5ODE2NTk0My4yOTcuMS4xNjk4MTY2MDA4LjU1LjAuMA'
              }
              alt=""
            />
          }
        ></MenuButton>
      </div>

      <div
        className={classNames(
          'flex h-[calc(100vh-64px)] flex-1 justify-center px-4 pt-4 lg:px-0 lg:pb-4 lg:pr-12 lg:pt-8',
          desktopSidebarCollapsed ? 'lg:pl-28' : 'lg:pl-72',
        )}
      >
        <CollapseButton
          open={desktopSidebarCollapsed}
          setOpen={setDesktopSidebarCollapsed}
        />
        <main className="h-full w-full max-w-screen-2xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const CollapseButton = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="absolute top-1/2 hidden h-6 w-4 -translate-y-1/2 transform cursor-pointer items-center justify-center transition-all duration-300 lg:flex"
      style={{ left: open ? '64px' : '240px' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Tooltip text={open ? 'Expand' : 'Collapse'} placement="right" hideArrow>
        {hover ? (
          <div
            className="flex h-6 w-4 items-center justify-center"
            onClick={() => setOpen(!open)}
          >
            {open ? (
              <ChevronRightIcon className="stroke-4 h-6 w-6 text-black opacity-50 [&>path]:stroke-[3]" />
            ) : (
              <ChevronLeftIcon className="stroke-4 h-6 w-6 text-black opacity-50 [&>path]:stroke-[3]" />
            )}
          </div>
        ) : (
          <div
            className="flex h-6 w-4 items-center justify-center"
            onClick={() => setOpen(!open)}
          >
            <div className="h-4 w-[2px] bg-gray-500"></div>
          </div>
        )}
      </Tooltip>
    </div>
  );
};
